import Cookies from "js-cookie";

export function getCurrentDateTime() {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentDate = new Date();
  const day = currentDate.getDate();
  const month = months[currentDate.getMonth()];
  const year = currentDate.getFullYear();
  let hours = currentDate.getHours();
  const minutes = currentDate.getMinutes();
  const seconds = currentDate.getSeconds();
  const ampm = hours >= 12 ? "pm" : "am";

  // Convert hours to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12;

  const formattedDateTime = `${day}th ${month} ${year}, ${hours
    .toString()
    .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")} ${ampm}`;

  return formattedDateTime;
}

export function removeCommasAndConvertToNumber(str) {
  if (str === null || str === undefined || str === "") {
    return NaN;
  }

  if (typeof str === "number") {
    return str;
  }

  const normalized = String(str).replace(/,/g, "");
  return parseFloat(normalized);
}

export const DELIVERY_COUNTRY_FEATURE_NAME = "Delivery Country";

export function getDeliveryCountry(features = []) {
  const feature = features?.find(
    (item) => item?.name === DELIVERY_COUNTRY_FEATURE_NAME
  );
  const value = feature?.value;
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function getLineItemDeliveryCountry(item) {
  return (
    getDeliveryCountry(item?.features) ||
    getDeliveryCountry(item?.product?.features) ||
    (typeof item?.delivery_country === "string"
      ? item.delivery_country.trim()
      : null)
  );
}

export function getCartDeliveryCountry(lineItems = []) {
  if (!Array.isArray(lineItems)) return null;

  for (const item of lineItems) {
    const country = getLineItemDeliveryCountry(item);
    if (country) return country;
  }

  return null;
}

export function mapCartItemsForDeliveryDialog(lineItems = []) {
  if (!Array.isArray(lineItems)) return [];

  return lineItems
    .filter((item) => getLineItemDeliveryCountry(item))
    .map((item) => ({
      id: item?.id,
      title: item?.title || "N/A",
      sku: item?.sku || "",
      img_url: item?.img_url || "",
      country: getLineItemDeliveryCountry(item),
    }));
}

export function mapLocalCartItemsForDialog(lineItems = []) {
  if (!Array.isArray(lineItems)) return [];

  return lineItems
    .filter((item) => !getLineItemDeliveryCountry(item))
    .map((item) => ({
      id: item?.id,
      title: item?.title || "N/A",
      sku: item?.sku || "",
      img_url: item?.img_url || "",
      country: null,
    }));
}

export async function checkDeliveryCountryConflict({
  productFeatures,
  cartId,
  storefront,
  authToken,
}) {
  const productCountry = getDeliveryCountry(productFeatures);

  if (!cartId) {
    return { allowed: true, productCountry, cartCountry: null };
  }

  try {
    const headers = {
      "Content-Type": "application/json",
      cartid: cartId,
    };
    if (authToken) {
      headers.Authorization = authToken;
    }

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND}/order/view_cart?storefront=${storefront}`,
      {
        method: "GET",
        headers,
      }
    );
    const cartData = await response.json();

    if (!response.ok) {
      return { allowed: true, productCountry, cartCountry: null };
    }

    const lineItems = cartData?.line_items || [];
    const deliveryCartItems = mapCartItemsForDeliveryDialog(lineItems);
    const localCartItems = mapLocalCartItemsForDialog(lineItems);
    const cartCountryFromItems = getCartDeliveryCountry(lineItems);
    const cartCountry =
      cartCountryFromItems || Cookies.get(CART_DELIVERY_COUNTRY_COOKIE);
    const hasLocalItems = localCartItems.length > 0;

    // Cart has Delivery Country items, but this product has none
    if (cartCountryFromItems && !productCountry) {
      return {
        allowed: false,
        conflictType: "local_into_foreign",
        productCountry: null,
        cartCountry: cartCountryFromItems,
        cartItems: deliveryCartItems,
      };
    }

    // Cart has local (no Delivery Country) items, but this product has a Delivery Country
    if (productCountry && !cartCountryFromItems && hasLocalItems) {
      return {
        allowed: false,
        conflictType: "foreign_into_local",
        productCountry,
        cartCountry: null,
        cartItems: localCartItems,
      };
    }

    // Both have countries, but they differ
    if (productCountry && cartCountry && cartCountry !== productCountry) {
      return {
        allowed: false,
        conflictType: "country_mismatch",
        productCountry,
        cartCountry,
        cartItems: deliveryCartItems,
      };
    }

    return { allowed: true, productCountry, cartCountry };
  } catch (error) {
    console.error("Delivery country check failed:", error?.message);
    return { allowed: true, productCountry, cartCountry: null };
  }
}

export const CART_DELIVERY_COUNTRY_COOKIE = "cart_delivery_country";

export function isForeignDeliveryCountry(country) {
  return Boolean(
    country && String(country).trim().toLowerCase() !== "pakistan"
  );
}

export function matchCountryByName(countries = [], countryName) {
  if (!countryName || !Array.isArray(countries)) return null;
  const needle = String(countryName).trim().toLowerCase();
  return (
    countries.find((country) => country?.text?.toLowerCase() === needle) ||
    countries.find((country) => country?.value?.toLowerCase() === needle) ||
    null
  );
}

export function setCartDeliveryCountryCookie(country) {
  if (country) {
    Cookies.set(CART_DELIVERY_COUNTRY_COOKIE, country, { expires: 365 });
    return;
  }

  Cookies.remove(CART_DELIVERY_COUNTRY_COOKIE);
}

export function clearCartDeliveryCountryCookie() {
  setCartDeliveryCountryCookie(null);
}

export function syncCartDeliveryCountryCookie(lineItems) {
  if (!Array.isArray(lineItems) || lineItems.length === 0) {
    clearCartDeliveryCountryCookie();
    return;
  }

  const country = getCartDeliveryCountry(lineItems);
  if (country) {
    setCartDeliveryCountryCookie(country);
  }
}
